function normalizeName(value = '') {
  return String(value).replace(/^@/, '').replace(/#[A-Z]$/i, '').trim().toUpperCase();
}

export function getFieldBinding(field = {}, fallback = '') {
  return field.binding || field.name || field.variableBase || normalizeName(field.variable || fallback);
}

function editableFields(fields = []) {
  return fields.flatMap((field) => {
    if (!field || field.visible === false) return [];
    if (
      field.disabled === true ||
      field.readonly === true ||
      field.readOnly === true ||
      field.capturable === false ||
      field.editable === false
    ) return [];
    if (field.type === 'field_row') return editableFields(field.parts || []);
    if (field.type === 'checkbox-group') {
      return editableFields((field.options || []).map((option) => ({
        ...option,
        type: 'checkbox',
        controlType: 'checkbox',
      })));
    }
    if (['button', 'system', 'display', 'logicReference'].includes(field.type)) return [];
    if (['display', 'label'].includes(String(field.controlType || '').toLowerCase())) return [];
    return [field];
  });
}

function valueFor(values, binding) {
  const wanted = normalizeName(binding);
  const match = Object.entries(values || {}).find(([name]) => normalizeName(name) === wanted);
  return match ? match[1] : undefined;
}

function isEmpty(value) {
  return value === undefined || value === null || String(value).trim() === '';
}

export function isSstCheckboxChecked(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === undefined || value === null) return false;
  return ['TRUE', '1', 'YES', 'Y', 'SI', 'S'].includes(String(value).trim().toUpperCase());
}

function validateDatatype(field, value) {
  if (isEmpty(value)) return '';
  const datatype = String(field.datatype || field.typedata || '').toUpperCase();
  const text = String(value).trim();

  if (['N', 'INT', 'INTEGER', 'NUM', 'NUMBER', 'NUMERIC'].includes(datatype) && !/^-?\d+$/.test(text)) {
    return 'Debe contener solamente numeros enteros.';
  }
  if (datatype === 'DECIMAL' && !/^-?\d+(?:[.,]\d+)?$/.test(text)) {
    return 'Debe ser un numero decimal valido.';
  }
  if (['DATE', 'DATE_SABRE'].includes(datatype) && !/^\d{2}[A-Z]{3}(?:\d{2,4})?$/i.test(text)) {
    return 'Usa el formato Sabre, por ejemplo 14OCT.';
  }
  if (['TIME', 'HOUR_SABRE'].includes(datatype) && !/^(?:[01]\d|2[0-3])[0-5]\d$/.test(text)) {
    return 'Usa hora de cuatro digitos en formato HHMM.';
  }
  if (datatype === 'MASK-NNNN' && !/^\d{4}$/.test(text)) {
    return 'Usa cuatro digitos.';
  }
  if (datatype === 'MASK-NNAAA' && !/^\d{2}[A-Z]{3}$/i.test(text)) {
    return 'Usa dos digitos y tres letras, por ejemplo 14OCT.';
  }
  if (['MAIL', 'EMAIL'].includes(datatype) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
    return 'Ingresa un correo valido.';
  }
  return '';
}

export function validateSstFieldValue(field = {}, value) {
  const required = field.required === true || field.mandatory === true || field.visualRequired === true;

  if (required && (isEmpty(value) || (field.controlType === 'checkbox' && !isSstCheckboxChecked(value)))) {
    return 'Este campo es obligatorio.';
  }

  const maxlength = Number(field.maxlength || field.length || 0);
  if (maxlength > 0 && String(value ?? '').length > maxlength) {
    return `Maximo ${maxlength} caracteres.`;
  }

  return validateDatatype(field, value);
}

export function validateSstForm(form = {}, values = {}) {
  const errors = {};

  const validateCheckboxGroups = (fields = []) => {
    fields.forEach((field) => {
      if (!field || field.visible === false) return;
      if (field.type === 'field_row') validateCheckboxGroups(field.parts || []);
      if (field.type !== 'checkbox-group') return;
      const options = (field.options || []).filter((option) => option?.visible !== false);
      if (field.requiredOne && options.length > 0 && !options.some((option) => {
        const binding = getFieldBinding(option, option.variable);
        return isSstCheckboxChecked(valueFor(values, binding));
      })) {
        const firstBinding = getFieldBinding(options[0], options[0]?.variable);
        if (firstBinding) errors[firstBinding] = 'Selecciona al menos una opcion.';
      }
    });
  };

  validateCheckboxGroups(form.fields || []);

  editableFields(form.fields || []).forEach((field) => {
    const binding = getFieldBinding(field, field.variable);
    if (!binding) return;
    const value = valueFor(values, binding);
    const error = validateSstFieldValue(field, value);
    if (error) errors[binding] = error;
  });

  return errors;
}

export function mergeFormDefaults(values = {}, form = {}) {
  const nextValues = { ...values };
  editableFields(form.fields || []).forEach((field) => {
    const binding = getFieldBinding(field, field.variable);
    if (!binding || valueFor(nextValues, binding) !== undefined) return;
    if (field.value !== undefined && field.value !== null && field.value !== '') nextValues[binding] = field.value;
    else if (field.controlType === 'checkbox') nextValues[binding] = false;
  });
  return nextValues;
}


